import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { DueDiligenceTier, DueDiligenceOperationType } from "../../../Common/Types-DTOs/DueDiligence";
import { isErr } from "../../../Common/Utils/Result";
import { getFormattedErrorMessage } from "../../../Common/Utils/errorMessages";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { useApi } from "../../../Common/Hooks/useApi";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { NewCasePayment } from "./PaymentSection";

/** What the backend may answer when paying with credits, as the user reads it. */
const CREDIT_PAYMENT_ERROR_KEYS: Record<string, string> = {
  INSUFFICIENT_CREDITS: "dueDiligence.newCaseModal.payment.errors.insufficientCredits",
  DUE_DILIGENCE_TIER_NOT_IN_PLAN: "dueDiligence.newCaseModal.payment.errors.tierNotInPlan",
};

export const useNewCaseForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createCase, payWithCredits } = useDueDiligence();
  const { getDueDiligencePlanCheckoutEndpoint } = useApi();
  const { resyncBusiness } = useBusiness();
  const [formData, setFormData] = useState<{
    name: string;
    propertyAddress: string;
    propertyType: string;
    cadastralReference: string;
    tier: DueDiligenceTier;
    operationType: DueDiligenceOperationType | "";
    observations: string;
    ownerName: string;
    ownerIdCard: string;
    acceptedTerms: boolean;
  }>({
    name: "",
    propertyAddress: "",
    propertyType: "",
    cadastralReference: "",
    tier: "basic",
    operationType: "",
    observations: "",
    ownerName: "",
    ownerIdCard: "",
    acceptedTerms: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("dueDiligence.newCaseForm.errors.nameRequired");
    }
    if (!formData.propertyAddress.trim()) {
      newErrors.propertyAddress = t("dueDiligence.newCaseForm.errors.propertyAddressRequired");
    }
    const cadastralReference = formData.cadastralReference.trim();
    if (!cadastralReference) {
      newErrors.cadastralReference = t("dueDiligence.newCaseForm.errors.cadastralReferenceRequired");
    } else if (cadastralReference.length !== 14 && cadastralReference.length !== 20) {
      newErrors.cadastralReference = t("dueDiligence.newCaseForm.errors.cadastralReferenceLength");
    }
    if (!formData.operationType) {
      newErrors.operationType = t("dueDiligence.newCaseForm.errors.operationTypeRequired");
    }
    if (!formData.acceptedTerms) {
      newErrors.acceptedTerms = t("dueDiligence.newCaseForm.errors.acceptedTermsRequired");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setSubmitAttempts(prev => prev + 1);
    }
    return Object.keys(newErrors).length === 0;
  };

  const notifyError = (message: string) =>
    eventBus.emit("NOTIFICATION", Notification.buildNotification("error", message));

  /**
   * Creates the case and takes it as far as the payment goes: paid with
   * credits, off to Stripe to subscribe, or pending payment on its page.
   * @param payment - How the case gets paid.
   */
  const handleSubmit = async (payment: NewCasePayment) => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const createResult = await createCase({
        name: formData.name,
        propertyAddress: formData.propertyAddress || undefined,
        propertyType: formData.propertyType || undefined,
        cadastralReference: formData.cadastralReference.trim() || undefined,
        tier: formData.tier,
        operationType: formData.operationType as DueDiligenceOperationType,
        observations: formData.observations || undefined,
        ownerName: formData.ownerName || undefined,
        ownerIdCard: formData.ownerIdCard || undefined,
      });

      if (isErr(createResult)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            getFormattedErrorMessage(createResult.error.errorCode, t("dueDiligence.newCaseForm.createErrorPrefix"))
          )
        );
        return;
      }

      const { id, alias } = createResult.value.case;
      const casePath = `/due-diligence/case/${alias}`;

      if (payment.kind === "credits") {
        const payResult = await payWithCredits(id);
        if (isErr(payResult)) {
          // The case stays pending payment, payable from its page
          notifyError(
            t(CREDIT_PAYMENT_ERROR_KEYS[payResult.error.errorCode] ?? "dueDiligence.newCaseModal.payment.errors.payFailed")
          );
        }
        resyncBusiness();
      }

      if (payment.kind === "plan") {
        const checkoutResult = await getDueDiligencePlanCheckoutEndpoint(payment.lookupKey, id);
        if (!isErr(checkoutResult)) {
          window.location.href = checkoutResult.value.url;
          return;
        }
        notifyError(t("dueDiligence.newCaseModal.payment.errors.checkoutFailed"));
      }

      navigate(casePath);
    } catch (error) {
      console.error("Error creating case:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("dueDiligence.newCaseForm.createErrorMessage"))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      propertyAddress: "",
      propertyType: "",
      cadastralReference: "",
      tier: "basic",
      operationType: "",
      observations: "",
      ownerName: "",
      ownerIdCard: "",
      acceptedTerms: false,
    });
    setErrors({});
  };

  return {
    formData,
    errors,
    isLoading,
    submitAttempts,
    handleInputChange,
    handleSubmit,
    resetForm,
  };
};
